import { globalTheme } from '@frontend/components/external-providers';
import { useCanvasGet, useCanvasUpdate, useCanvasView } from '@frontend/editor/data-access';
import {
  FollowCheckBranchesEnum,
  FollowCheckCellType,
} from '@frontend/editor/interface';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { Typography } from '@mui/material';
import { get } from 'lodash';
import { memo, useMemo } from 'react';
import { Trans } from 'react-i18next';
import { NodeProps, Position, useViewport } from 'reactflow';
import EditorBlockBorder from '../editor-block-border/editor-block-border';
import EditorBlockContainer from '../editor-block-container/editor-block-container';
import { HandlePoint } from '../handle-point/handle-point';
import { NodeHeader } from '../node-header/node-header';
import { BodyStyled, ItemWrapperStyled } from './styles';

// 追蹤檢查節點(IG限定)：固定「已追蹤/尚未追蹤」兩個分支，本身無可設定項
export const NodeFollowCheck = ({ id, selected }: NodeProps) => {
  const { zoom } = useViewport();
  const { getTargetElement } = useCanvasGet();
  const { canvasUpdateLabel } = useCanvasUpdate();
  const { onHoverCellID, onHoverElement } = useCanvasView();

  const { header, inputID, children } = useMemo(() => {
    const elementData = getTargetElement(id);
    return {
      header: get(elementData, 'label', ''),
      inputID: get(elementData, 'inputID', ''),
      children: get(elementData, 'children', []) as string[],
    };
  }, [getTargetElement, id]);

  return (
    <EditorBlockContainer nodeID={id}>
      <HandlePoint
        id={inputID}
        type="target"
        position={Position.Left}
        styles={{
          top: '5%',
        }}
        isConnected={false}
      />
      <EditorBlockBorder
        zoom={zoom}
        nodeID={id}
        selected={selected}
        color={get(globalTheme, 'palette.orange.600', '')}
      >
        <NodeHeader
          background={get(globalTheme, 'palette.orange.50', '')}
          color={get(globalTheme, 'palette.orange.600', '')}
          icon={<PersonAddAlt1Icon fontSize="small" />}
          title={header}
          onBlur={(val) => canvasUpdateLabel(id, val)}
        />
        <BodyStyled>
          {children.map((cellID: string) => {
            const cellData = getTargetElement(cellID) as FollowCheckCellType;
            // 刪除/undo 的瞬間狀態可能拿不到 cell，直接存取會讓整個 canvas 崩潰
            if (!cellData) return null;
            return (
              <ItemWrapperStyled
                key={cellID}
                $isHover={onHoverCellID === cellID}
                onMouseEnter={() => onHoverElement(cellID, 2)}
                onMouseLeave={() => onHoverElement('', 0)}
              >
                <HandlePoint
                  id={cellData.outputID}
                  type="source"
                  position={Position.Right}
                  styles={{
                    top: '50%',
                  }}
                  isConnected={false}
                />
                <Typography variant="subtitle2" color="grey.900">
                  {cellData.branch === FollowCheckBranchesEnum.FOLLOWING ? (
                    <Trans i18nKey="canvas.followCheck.following" />
                  ) : (
                    <Trans i18nKey="canvas.followCheck.notFollowing" />
                  )}
                </Typography>
              </ItemWrapperStyled>
            );
          })}
        </BodyStyled>
      </EditorBlockBorder>
    </EditorBlockContainer>
  );
};

export default memo(NodeFollowCheck);
